import { useState, useEffect, useCallback, useRef } from 'react';
import onScan from 'onscan.js';
import api from '../api/client';

/**
 * Hook for keyboard-wedge barcode scanner detection via onScan.js.
 *
 * Attaches onScan to `document` on mount and detaches on unmount.
 * When a barcode is scanned, performs a SKU lookup through the REST API
 * and invokes the appropriate callback.
 *
 * @param {Object}   options
 * @param {Function} options.onProductScanned - Called with the product object when a SKU matches.
 * @param {Function} options.onScanError      - Called with the scanned code string when no product is found.
 * @param {boolean}  [options.enabled=true]   - Whether the scanner listener is active.
 * @returns {{ lastScan: { code: string, product: Object|null, error: string|null, timestamp: number }|null, isAttached: boolean }}
 */
export default function useBarcode({
  onProductScanned,
  onScanError,
  enabled = true,
} = {}) {
  const [lastScan, setLastScan] = useState(null);
  const [isAttached, setIsAttached] = useState(false);

  // Keep stable references to the latest callbacks so the onScan handler
  // always invokes the current version without needing to re-attach.
  const onProductScannedRef = useRef(onProductScanned);
  const onScanErrorRef = useRef(onScanError);

  useEffect(() => {
    onProductScannedRef.current = onProductScanned;
  }, [onProductScanned]);

  useEffect(() => {
    onScanErrorRef.current = onScanError;
  }, [onScanError]);

  /**
   * Handle a scanned barcode string: look up the product by SKU and
   * invoke the appropriate callback.
   */
  const handleScan = useCallback(async (scannedCode) => {
    const code = typeof scannedCode === 'string' ? scannedCode.trim() : String(scannedCode).trim();

    if (!code) {
      return;
    }

    try {
      const product = await api.getProductBySku(code);

      setLastScan({
        code,
        product,
        error: null,
        timestamp: Date.now(),
      });

      if (typeof onProductScannedRef.current === 'function') {
        onProductScannedRef.current(product);
      }
    } catch (err) {
      const message = err?.message || 'Product not found';

      setLastScan({
        code,
        product: null,
        error: message,
        timestamp: Date.now(),
      });

      if (typeof onScanErrorRef.current === 'function') {
        onScanErrorRef.current(code);
      }
    }
  }, []);

  // ---------------------------------------------------------------
  // Attach / detach onScan based on `enabled`
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled) {
      // If transitioning from enabled -> disabled, detach.
      try {
        onScan.detachFrom(document);
      } catch {
        // Not currently attached — nothing to do.
      }
      setIsAttached(false);
      return;
    }

    // Detach first in case a previous instance is still bound (e.g. hot
    // reload during development or rapid enable/disable toggling).
    try {
      onScan.detachFrom(document);
    } catch {
      // Not currently attached — safe to ignore.
    }

    try {
      onScan.attachTo(document, {
        suffixKeyCodes: [13], // Enter key terminates the barcode
        reactToPaste: true,   // Support paste-based scanners
        minLength: 3,         // Ignore accidental short input
        onScan: (sCode) => {
          handleScan(sCode);
        },
      });
      setIsAttached(true);
    } catch {
      // Attachment failed (e.g. onScan already attached and detach
      // was unsuccessful). Surface as not-attached.
      setIsAttached(false);
    }

    return () => {
      try {
        onScan.detachFrom(document);
      } catch {
        // Already detached — ignore.
      }
      setIsAttached(false);
    };
  }, [enabled, handleScan]);

  return { lastScan, isAttached };
}
